import './styles.css';
import { IconButton, TextField } from '@material-ui/core';
import { Button, Col, InputGroup, Label, Row, Table } from 'reactstrap';
import { v4 as uuid } from 'uuid';
import {
  FaChevronDown,
  FaChevronUp,
  FaPlusCircle,
  FaTrash,
} from 'react-icons/fa';
import { useEffect, useMemo, useState } from 'react';
import formatCurrency from 'utils/formatCurrency';
import { arredondaDecimais } from 'utils/arredondaDecimais';
import { iPropostaDados } from '../../../..';
import gerarParcelasDefault from '../../../MeioPagamento/components/GerarParcelasDefault';
import { Td, Th } from './style';

type Grupo = {
  id: string;
  titulo: string;
  valorTotal: number;
  parcelas: any[];
  itens: any[];
};

export default function ParcelasProduto({
  propostaVersao,
  onChange,
  isModoSimulacao,
  isAnalisador,
}: {
  propostaVersao: iPropostaDados;
  onChange: (a: iPropostaDados) => void;
  isModoSimulacao: boolean;
  isAnalisador: boolean | false;
}): JSX.Element {
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [valoresDigitados, setValoresDigitados] = useState<
    Record<string, string>
  >({});
  const EPSILON = 1e-6; // tolerância, pois o número pode ser perto de zero e nunca ser zero

  const grupos: Grupo[] = useMemo(() => {
    const gruposMap = propostaVersao.precificacao.reduce((acc, item) => {
      if (item.tipo === 'A') return acc;

      const key = item.produtoCategoriaSubProj;

      if (!acc[key]) {
        acc[key] = {
          id: key,
          titulo: key ?? '',
          itens: [],
          valorTotal: 0,
          parcelas: [],
        };
      }

      acc[key].itens.push(item);
      acc[key].valorTotal += item.valorFinalComOutros || 0;

      return acc;
    }, {} as Record<string, Grupo>);

    Object.values(gruposMap).forEach((grupo: any) => {
      // eslint-disable-next-line no-param-reassign
      grupo.parcelas = (propostaVersao.parcelas || []).filter(
        p => p.setorAtividade === grupo.id,
      );
    });

    return Object.values(gruposMap);
  }, [propostaVersao.precificacao, propostaVersao.parcelas]);

  function toggleRow(id: string) {
    setExpandedRows(prev =>
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id],
    );
  }

  function calcularPorcentagem(valor: number, porcentagem: number): number {
    return (valor * porcentagem) / 100;
  }

  function adicionarParcela(grupoId: string) {
    const isRecursoProprio = propostaVersao.meioPagamento.meioPagamento === 1;

    const novasParcelas = [
      ...propostaVersao.parcelas,
      {
        percentual: 0,
        valor: 0,
        observacao: ' - ',
        meioPagamentoId: 1,
        id: 1,
        idTemp: uuid(),
        setorAtividade: grupoId,
        dias: null,
        createdAt: new Date(),
      },
    ];

    const parcelasFinal = isRecursoProprio
      ? novasParcelas
      : gerarParcelasDefault(propostaVersao, 1);

    if (!isRecursoProprio) {
      parcelasFinal.push({
        percentual: 0,
        valor: 0,
        observacao: '',
        meioPagamentoId: 1,
        id: 1,
        idTemp: uuid(),
        setorAtividade: grupoId,
        dias: null,
        createdAt: new Date(),
      });
    }

    onChange({
      ...propostaVersao,
      parcelas: parcelasFinal,
      outros: {
        ...propostaVersao.outros,
        isParcelamentoEspecial: true,
        isRegramentoGeral: propostaVersao.outros?.isRegramentoGeral ?? false,
      },
      meioPagamento: {
        bancoProposta: '',
        meioPagamento: 1,
        banco: '',
        formatoBanco: '',
        linhaCredito: 0,
      },
    });
  }

  function removerParcela(tempId: string, id: number) {
    const parcelaRemovida = propostaVersao.parcelas.find(
      p => p.idTemp === tempId && p.id === id,
    );

    const parcelasAtualizadas = propostaVersao.parcelas.filter(
      p => p.idTemp !== tempId || p.id !== id,
    );

    const setorAtividadeRemovido = parcelaRemovida?.setorAtividade;

    const aindaExisteParcelaNoSetor = parcelasAtualizadas.some(
      p => p.setorAtividade === setorAtividadeRemovido,
    );

    const totalParcelasComSetor = grupos.reduce((acc, grupo) => {
      if (!grupo.id) return acc;

      const parcelasDoGrupo = parcelasAtualizadas.filter(
        p => p.setorAtividade === grupo.id,
      );

      const temAlgumaParcelaComValor = parcelasDoGrupo.some(
        p => (p.valor || 0) > 0,
      );

      if (temAlgumaParcelaComValor) {
        return acc + (grupo.valorTotal || 0);
      }

      return acc;
    }, 0);

    const valorBase = propostaVersao.valores.valorFinalComOutros;

    const novoValorFinal = valorBase - totalParcelasComSetor;

    // Se o setor ficou sem parcelas, vou gerar as parcelas default
    if (setorAtividadeRemovido && !aindaExisteParcelaNoSetor) {
      const parcelasComSetor = parcelasAtualizadas.filter(
        p => p.setorAtividade,
      );

      const propostaAtualizada = {
        ...propostaVersao,
        parcelas: parcelasAtualizadas,
        valores: {
          ...propostaVersao.valores,
          valorFinalParcelas: novoValorFinal,
        },
      };

      const parcelasDefault = gerarParcelasDefault(propostaAtualizada, 1);

      onChange({
        ...propostaAtualizada,
        parcelas: [...parcelasComSetor, ...parcelasDefault],
      });

      return;
    }

    onChange({
      ...propostaVersao,
      parcelas: parcelasAtualizadas,
      valores: {
        ...propostaVersao.valores,
        valorFinalParcelas: novoValorFinal,
      },
    });
  }
  // Caso não tenha parcelas com setorAtividade, então não é parcelamento especial
  useEffect(() => {
    const temParcelasEspeciais = propostaVersao.parcelas?.some(
      p => p.setorAtividade != null,
    );

    if (
      propostaVersao.outros?.isParcelamentoEspecial === temParcelasEspeciais
    ) {
      return;
    }

    onChange({
      ...propostaVersao,
      outros: {
        ...propostaVersao.outros,
        isParcelamentoEspecial: temParcelasEspeciais,
        isRegramentoGeral: propostaVersao.outros?.isRegramentoGeral ?? false,
      },
    });
  }, [propostaVersao.parcelas]);

  function handleParcelaChange(
    idTemp: string,
    campo: string,
    valorDigitado: any,
    id: number,
  ) {
    const percentComissao = propostaVersao.valores.percentualComissao;
    let setor = '';

    const parcelasAtualizadas = propostaVersao.parcelas.map(p => {
      if (!p.setorAtividade) {
        return {
          ...p,
          valor: 0,
          percentual: 0,
        };
      }
      if (p.idTemp !== idTemp || p.id !== id) return p;

      const valorTotalGrupo =
        grupos.find(g => g.id === p.setorAtividade)?.valorTotal ?? 0;

      let novoValor = p.valor;
      let novoPercentual = p.percentual;

      if (campo === 'valor') {
        setor = p.setorAtividade;
        novoValor = Number(valorDigitado ?? 0);
        novoPercentual =
          valorTotalGrupo > 0 ? (novoValor * 100) / valorTotalGrupo : 0;
      }

      if (campo === 'percentual') {
        setor = p.setorAtividade;
        novoPercentual = Number(valorDigitado ?? 0);
        novoValor = (novoPercentual * valorTotalGrupo) / 100;
      }

      if (campo !== 'valor' && campo !== 'percentual') {
        setor = p.setorAtividade;
        return { ...p, [campo]: valorDigitado };
      }

      return {
        ...p,
        valor: novoValor,
        percentual: novoPercentual,
        comissao: novoValor * (percentComissao / 100),
      };
    });

    const totalParcelasComSetor = grupos.reduce((acc, grupo) => {
      if (!grupo.id) return acc;

      const parcelasDoGrupo = parcelasAtualizadas.filter(
        p => p.setorAtividade === grupo.id,
      );

      const temAlgumaParcelaComValor = parcelasDoGrupo.some(
        p => (p.valor || 0) > 0,
      );

      if (temAlgumaParcelaComValor) {
        return acc + (grupo.valorTotal || 0);
      }

      return acc;
    }, 0);

    const valorBase = propostaVersao.valores.valorFinalComOutros;

    const novoValorFinal = valorBase - totalParcelasComSetor;

    onChange({
      ...propostaVersao,
      parcelas: parcelasAtualizadas,
      valores: {
        ...propostaVersao.valores,
        valorFinalParcelas: novoValorFinal,
      },
    });
  }

  function renderContent(tipo: string, config: any): React.ReactNode[] {
    const isExpanded = expandedRows.includes(config.id);
    const toDate = (value: string | Date) =>
      value instanceof Date ? value : new Date(value.replace(' ', 'T'));

    const parcelas = (propostaVersao.parcelas || [])
      .filter(p => p.setorAtividade === config.id)
      .sort(
        (a: any, b: any) =>
          toDate(a.createdAt).getTime() - toDate(b.createdAt).getTime(),
      );

    const titulo = config.itens[0].produtoCategoriaSubProj;
    const valorTotalDasParcelas = parcelas.reduce(
      (acc, item) => acc + item.valor,
      0,
    );
    const percentualTotalDasParcelas = parcelas.reduce(
      (acc, item) => acc + item.percentual,
      0,
    );
    const valorSaldo = config.valorTotal - valorTotalDasParcelas;

    const rows = [
      <tr key={`${tipo}-${config.id}`}>
        <Td
          colSpan={5}
          style={{
            background: '#e5e7eb',
            padding: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <IconButton
            onClick={() => toggleRow(config.id)}
            size="small"
            className="mr-2"
          >
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          </IconButton>
          <div className="mr-2">{titulo}</div>
          <div>Valor total: {formatCurrency(config.valorTotal)}</div>
        </Td>
      </tr>,
    ];

    if (isExpanded) {
      rows.push(
        <tr key={`details-${config.id}`}>
          <Td colSpan={5} style={{ padding: 16 }}>
            <Table
              bordered
              responsive
              size="sm"
              style={{ tableLayout: 'fixed', width: '100%' }}
            >
              <thead>
                <tr className="text-center">
                  <th style={{ width: isAnalisador ? '20%' : '10%' }}>DIAS</th>
                  <th style={{ width: isAnalisador ? '20%' : '10%' }}>%</th>
                  <th style={{ width: isAnalisador ? '25%' : '20%' }}>VALOR</th>
                  <th style={{ width: isAnalisador ? '25%' : '15%' }}>
                    COMISSÃO({propostaVersao.valores.percentualComissao}%)
                  </th>
                  <th style={{ width: '35%' }} hidden={isAnalisador}>
                    EVENTO
                  </th>
                  <th
                    style={{
                      width: '10%',
                      textAlign: 'center',
                    }}
                  >
                    <Button
                      outline
                      className="btn btn-default"
                      title="Adicionar Parcela"
                      type="button"
                      color="default"
                      size="sm"
                      hidden={!isModoSimulacao}
                      onClick={() => adicionarParcela(config.id)}
                    >
                      <FaPlusCircle />
                    </Button>
                  </th>
                </tr>
              </thead>

              <tbody>
                {parcelas.map((p: any, index: number) => {
                  const isEditing = valoresDigitados[index] !== undefined;
                  return (
                    <tr key={p.idTemp}>
                      <Td className="text-center">
                        <Label
                          className={`${
                            isAnalisador ? `` : `pt-3 pr-4`
                          } text-muted`}
                        >
                          {isModoSimulacao ? (
                            <TextField
                              type="text"
                              style={{ width: '100%' }}
                              value={p.dias}
                              onChange={e =>
                                handleParcelaChange(
                                  p.idTemp,
                                  'dias',
                                  String(e.target.value),
                                  p.id,
                                )
                              }
                            />
                          ) : (
                            <strong>{p.dias}</strong>
                          )}
                        </Label>
                      </Td>

                      <Td className="text-center">
                        <Label
                          className={`${
                            isAnalisador ? `` : `pt-3 pr-4`
                          } text-muted`}
                        >
                          {isModoSimulacao ? (
                            <TextField
                              type="number"
                              style={{ width: '100%' }}
                              InputProps={{
                                endAdornment: <span>%</span>,
                              }}
                              value={p.percentual}
                              onChange={e =>
                                handleParcelaChange(
                                  p.idTemp,
                                  'percentual',
                                  Number(e.target.value),
                                  p.id,
                                )
                              }
                            />
                          ) : (
                            <strong>{p.percentual}</strong>
                          )}
                        </Label>
                      </Td>

                      <Td className="text-center">
                        <Label
                          className={`${
                            isAnalisador ? `` : `pt-3 pr-4`
                          } text-muted`}
                        >
                          {isModoSimulacao ? (
                            <TextField
                              type="text"
                              style={{ width: '100%' }}
                              value={
                                isEditing
                                  ? valoresDigitados[index]
                                  : formatCurrency(p.valor ?? 0, 2)
                              }
                              onChange={e => {
                                const texto = e.target.value;

                                setValoresDigitados(prev => ({
                                  ...prev,
                                  [index]: texto,
                                }));
                              }}
                              onBlur={e => {
                                const texto = e.target.value || '0';

                                const numero = Number(
                                  texto
                                    .replace(/\./g, '')
                                    .replace(',', '.')
                                    .replace('R$', '')
                                    .trim(),
                                );

                                handleParcelaChange(
                                  p.idTemp,
                                  'valor',
                                  numero,
                                  p.id,
                                );

                                setValoresDigitados(prev => {
                                  const novo = { ...prev };
                                  delete novo[index];
                                  return novo;
                                });
                              }}
                            />
                          ) : (
                            <strong>{formatCurrency(p.valor ?? 0, 2)}</strong>
                          )}
                        </Label>
                      </Td>

                      <Td
                        className="text-center"
                        hidden={propostaVersao.usuario.isIsentoComissao}
                      >
                        <Label
                          className={`${
                            isAnalisador ? `pt-3` : `pt-3 pr-4`
                          } text-muted`}
                        >
                          <strong>
                            {formatCurrency(
                              calcularPorcentagem(
                                p.valor,
                                (propostaVersao.valores.percentualComissao ??
                                  0) +
                                  (propostaVersao.valores
                                    .percentualAjusteComissao ?? 0),
                              ),
                              2,
                            )}
                          </strong>
                        </Label>
                      </Td>

                      <Td hidden={isAnalisador}>
                        <Label
                          className={`${
                            isAnalisador ? `` : `pt-3 pr-4`
                          } text-muted`}
                          style={{ width: '100%', display: 'block' }}
                        >
                          {isModoSimulacao ? (
                            <TextField
                              type="text"
                              style={{ width: '100%' }}
                              value={p.observacao}
                              onChange={e =>
                                handleParcelaChange(
                                  p.idTemp,
                                  'observacao',
                                  e.target.value,
                                  p.id,
                                )
                              }
                            />
                          ) : (
                            <p
                              style={{
                                whiteSpace: 'pre-wrap',
                                wordWrap: 'break-word',
                                width: '100%',
                                margin: 0,
                              }}
                            >
                              {p.observacao}
                            </p>
                          )}
                        </Label>
                      </Td>

                      <Td hidden={!isModoSimulacao}>
                        <Label
                          className={`${
                            isAnalisador ? `` : `pt-3 pr-4`
                          } text-muted`}
                        >
                          <InputGroup>
                            <Button
                              outline
                              className="btn-icon btn-2 ml-1"
                              color="danger"
                              type="button"
                              title="Remover Parcela"
                              size="sm"
                              onClick={() => removerParcela(p.idTemp, p.id)}
                            >
                              <FaTrash />
                            </Button>
                          </InputGroup>
                        </Label>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <Td className="text-right" colSpan={3}>
                    <Label
                      className={`${
                        isAnalisador ? `` : `pt-3 pr-4`
                      } text-muted`}
                    >
                      <strong>{percentualTotalDasParcelas}%</strong>
                    </Label>
                  </Td>
                  <Td className="text-left" colSpan={3}>
                    <Label
                      className={`${
                        isAnalisador ? `` : `pt-3 pr-4`
                      } text-muted`}
                    >
                      <strong>
                        Total: {formatCurrency(valorTotalDasParcelas, 2)}
                      </strong>
                    </Label>
                  </Td>
                </tr>
                {Math.abs(valorSaldo) > EPSILON &&
                  percentualTotalDasParcelas !== 0 && (
                    <tr>
                      <Td colSpan={2} />
                      <Td className="text-left" colSpan={4}>
                        <Label
                          className={`${
                            isAnalisador ? `` : `pt-3 pr-4`
                          } text-muted`}
                        >
                          <strong>
                            Saldo:{' '}
                            <span
                              style={{
                                color: valorSaldo >= 0 ? '' : 'red',
                              }}
                            >
                              R$ {valorSaldo >= 0 ? '-' : '+'}
                              {formatCurrency(
                                Math.abs(arredondaDecimais(valorSaldo)),
                                2,
                                false,
                                false,
                              )}
                            </span>
                          </strong>
                        </Label>
                      </Td>
                    </tr>
                  )}
              </tfoot>
            </Table>
          </Td>
        </tr>,
      );
    }

    return rows;
  }

  return (
    <Row>
      <Col>
        {propostaVersao.parcelas.length > 0 && (
          <Table responsive bordered className="mt-2 resumo-table">
            <tbody>
              {grupos.map(group => renderContent('precificacao', group))}
            </tbody>
          </Table>
        )}
      </Col>
    </Row>
  );
}
